<template>
    <div class="product-gallery" v-if="imageList.length > 0">
        <div class="product-gallery-paddingright">
            <div
                class="main-image-wrapper"
                @mouseenter="enableZoom"
                @mouseleave="disableZoom"
                @mousemove="handleMouseMove"
            >
                <div class="main-image" ref="mainImageRef">
                    <transition name="fade" mode="out-in">
                        <img :src="currentImage" :key="currentIndex" loading="eager" />
                    </transition>
                    <div
                        v-if="isZoomActive"
                        class="zoom-lens"
                        :style="lensStyle"
                    ></div>
                </div>
            </div>
        </div>

        <div class="thumbnail-gallery">
            <div
                v-for="(item, index) in imageList"
                :key="`${item}-${index}`"
                class="thumbnail-item"
            >
                <img
                    :src="item"
                    :class="{ active: currentIndex === index }"
                    @click="handleImageClick(index)"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                />
            </div>
        </div>
        <teleport to="body">
            <div
                v-if="isZoomActive"
                class="zoom-view"
                :style="zoomViewStyle"
            ></div>
        </teleport>
    </div>

    <div class="product-gallery" v-else>
        还没有上传图片
    </div>
</template>
<script setup lang="ts" name="Swipper">
    import { computed, ref, watch } from 'vue'
    import type { Article } from '~/types'

    const currentIndex = ref(0)
    const currentImage = ref('')
    
    const props = defineProps<{
        article: Article
    }>()

    const imageList = computed(() => {
        if (!props.article.contentImgs) return []
        return props.article.contentImgs.split(',')
    })

    watch(
        imageList,
        (list) => {
            if (list.length > 0) {
                currentIndex.value = 0
                currentImage.value = list[0] || ''
            } else {
                currentIndex.value = 0
                currentImage.value = ''
            }
        },
        { immediate: true }
    )

    const handleImageClick = (index: number) => {
        if (!imageList.value.length) return
        currentIndex.value = index
        currentImage.value = imageList.value[index] || ''
    }

    const mainImageRef = ref<HTMLElement | null>(null)
    const isZoomActive = ref(false)
    const lensSize = 140
    const zoomScale = 2.4
    const lensPosition = ref({ x: 0, y: 0 })
    const mainImageSize = ref({ width: 0, height: 0 })
    const zoomViewPosition = ref({ x: 0, y: 0 })

    const updateZoomViewPosition = () => {
        if (!mainImageRef.value) return
        const rect = mainImageRef.value.getBoundingClientRect()
        const offset = 24
        const zoomWidth = 600
        const zoomHeight = 600

        let left = rect.right + offset
        let top = rect.top

        // if (left + zoomWidth > window.innerWidth) {
        //     left = rect.left - zoomWidth - offset
        // }

        // if (top + zoomHeight > window.innerHeight) {
        //     top =  offset
        // }

        zoomViewPosition.value = { x: left, y: top }
    }

    const enableZoom = () => {
        if (imageList.value.length > 0) {
            isZoomActive.value = true
            updateZoomViewPosition()
        }
    }

    const disableZoom = () => {
        isZoomActive.value = false
    }

    const handleMouseMove = (event: MouseEvent) => {
        if (!isZoomActive.value || !mainImageRef.value) return
        const rect = mainImageRef.value.getBoundingClientRect()
        mainImageSize.value = { width: rect.width, height: rect.height }
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top
        const lensX = Math.max(0, Math.min(x - lensSize / 2, rect.width - lensSize))
        const lensY = Math.max(0, Math.min(y - lensSize / 2, rect.height - lensSize))
        lensPosition.value = { x: lensX, y: lensY }
        updateZoomViewPosition()
    }

    const lensStyle = computed(() => ({
        left: `${lensPosition.value.x}px`,
        top: `${lensPosition.value.y}px`,
        width: `${lensSize}px`,
        height: `${lensSize}px`
    }))

    const zoomViewStyle = computed(() => {
        if (!currentImage.value) return {}
        const width = mainImageSize.value.width || 1
        const height = mainImageSize.value.height || 1
        return {
            left: `${zoomViewPosition.value.x}px`,
            top: `${zoomViewPosition.value.y}px`,
            backgroundImage: `url(${currentImage.value})`,
            backgroundSize: `${width * zoomScale}px ${height * zoomScale}px`,
            backgroundPosition: `${-lensPosition.value.x * zoomScale}px ${-lensPosition.value.y * zoomScale}px`
        }
    })
</script>
<style scoped lang="scss">
    .product-gallery {
        flex: 6;
        display: flex;
        flex-direction: column;
        gap: 18px;
        .product-gallery-paddingright{
            padding-right:24px;
        }
        @media screen and (max-width:1500px) {
            .product-gallery-paddingright{
                padding-right:12px;
            }
        }
        @media screen and (max-width:1500px) {
            .product-gallery-paddingright{
                padding-right:0px;
            }
        }
    }

    .main-image-wrapper {
        display: flex;
        gap: 20px;
        align-items: stretch;
        position: relative;
    }

    .main-image {
        position: relative;
        border: 1px solid #e5e7eb;
        background-color: #fff;
        min-height: 420px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        img {
            width: 100%;
            object-fit: contain;
            display: block;
        }
    }

    .zoom-lens {
        position: absolute;
        border: 2px solid rgba(5, 150, 105, 0.7);
        background-color: rgba(255, 255, 255, 0.15);
        pointer-events: none;
        border-radius: 8px;
    }

    .zoom-view {
        position: fixed;
        width: 600px;
        height: 600px;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background-repeat: no-repeat;
        background-color: #fff;
        box-shadow: 0 20px 45px rgba(15, 23, 42, 0.15);
        pointer-events: none;
        z-index: 2000;
    }

    .thumbnail-gallery {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    .thumbnail-item {
        padding: 4px;
        border: 1px dashed #e5e7eb;
        transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    .thumbnail-item img {
        width: 78px;
        object-fit: cover;
        cursor: pointer;
        transition: transform 0.2s ease;
    }

    .thumbnail-item img.active {
        border: 2px solid #059669;
        padding: 2px;
        background-color: #ecfdf5;
    }

    .thumbnail-item:hover {
        border-color: #059669;
        background-color: #f0fdf4;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }
    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    @media (max-width: 1024px) {
        .main-image-wrapper {
            flex-direction: column;
        }

        .zoom-view {
            width: 480px;
            height: 480px;
        }
    }
</style>